import React, { useEffect, useState } from "react";
import {
  FaTrash,
  FaEdit,
  FaEye,
  FaPhoneAlt,
  FaFilter,
  FaWallet,
  FaPlus,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import AllUserListModal from "../../components/Modals/user/AllUserListModal";
import DeleteModal from "../../components/Modals/user/DeleteModal";
import WalletModal from "../../components/Modals/WalletModal";
import { LuCalculator } from "react-icons/lu";

import { Link } from "react-router-dom";
import {
  AlluserList,
  deleteUser,
  updateUserStatus,
  getUserWallet,
  getUserById,
  addUserDepositAmount,
} from "../../api/endpoints";
import Swal from "../../utils/swalCompat";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/PageHeader";

// ✅ AddDepositModal Component with Confirmation and 3 Types
const AddDepositModal = ({ show, setShow, userId, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("DEPOSIT");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!amount || amount <= 0) {
      Swal.fire("Error", "Please enter a valid amount greater than 0", "error");
      return;
    }

    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      html: `
        <div style="text-align: left; font-size: 16px;">
          <p><strong>User ID:</strong> ${userId}</p>
          <p><strong>Amount:</strong> ₹${Number(amount).toLocaleString()}</p>
          <p><strong>Type:</strong> ${type}</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Add it!",
      cancelButtonText: "Cancel",
    });

    if (!confirmResult.isConfirmed) return;

    try {
      setLoading(true);

      const res = await addUserDepositAmount({
        userId,
        amount: Number(amount),
        type: type,
      });
      console.log("Add Amount Response:", res);

      const successMessage =
        type === "DEPOSIT"
          ? `₹${amount} deposited successfully`
          : type === "BONUS"
            ? `₹${amount} bonus added successfully`
            : `₹${amount} penalty added successfully`;

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: successMessage,
        timer: 2000,
        showConfirmButton: false,
      });

      setAmount("");
      setType("DEPOSIT");
      setShow(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Add Amount Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to add amount",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`modal fade ${show ? "show d-block" : ""}`}
      style={{ backgroundColor: show ? "rgba(0,0,0,0.5)" : "none" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Wallet Amount</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShow(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label fw-bold">Amount (₹)</label>
              <input
                type="number"
                className="form-control form-control-lg"
                placeholder="e.g. 500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Type</label>
              <select
                className="form-select form-select-lg"
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={loading}
              >
                <option value="DEPOSIT">Deposit</option>
                <option value="BONUS">Bonus</option>
                <option value="PENALTY">Penalty</option>
              </select>
              <small className="text-muted mt-1 d-block">
                {type === "DEPOSIT" && "Add money to user's deposit balance"}
                {type === "BONUS" && "Add bonus amount to user's bonus balance"}
                {type === "PENALTY" &&
                  "Deduct penalty from user's winning balance"}
              </small>
            </div>
          </div>
          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={() => setShow(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="btn btn-success"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                ></span>
              ) : (
                `Add ${type}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------------------------

const AllUserList = () => {
  const navigate = useNavigate();

  const [showAllUserListModal, setShowAllUserListModal] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [AllusersList, setAllusersList] = useState([]);
  const [editData, setEditData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletData, setWalletData] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [kycStatus, setKycStatus] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  const [showAddDepositModal, setShowAddDepositModal] = useState(false);
  const [depositUserId, setDepositUserId] = useState(null);

  // ✅ Filter states for image data
  const [categoryFilter, setCategoryFilter] = useState("");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");

  const userList = async () => {
    try {
      setLoading(true);

      const params = {
        page,
        limit,
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      if (status) {
        params.status = status;
      }

      if (kycStatus) {
        params.kycStatus = kycStatus;
      }

      // ✅ Add category/value filters
      if (categoryFilter) {
        params.category = categoryFilter;
      }

      if (minValue) {
        params.minValue = minValue;
      }

      if (maxValue) {
        params.maxValue = maxValue;
      }

      const res = await AlluserList(params);
      console.log("User List Response:", res);

      const responseData = res.data?.data || res.data;

      setAllusersList(responseData.users || []);
      setTotalPages(responseData.pagination?.totalPages || 1);
      setTotalItems(responseData.pagination?.totalItems || 0);

      if (responseData.pagination?.page) {
        setPage(responseData.pagination.page);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      Swal.fire("Error", "Failed to fetch users", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    userList();
  }, [search, status, kycStatus, categoryFilter, minValue, maxValue, page]);

  const handleDelete = async () => {
    try {
      await deleteUser({ userId: deleteId });
      setShowDeleteModal(false);
      userList();
    } catch (error) {
      console.error("DELETE ERROR:", error);
      Swal.fire("Error", "Failed to delete user", "error");
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      setTogglingId(userId);
      await updateUserStatus({ userId, status: newStatus });
      userList();
    } catch (error) {
      console.error("STATUS UPDATE ERROR:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update user status",
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  };

  const openWalletModal = async (user) => {
    try {
      setWalletLoading(true);
      const response = await getUserWallet({ userId: user._id });
      console.log("Wallet API Response:", response);

      const walletDataFromAPI = response.data || response;

      setWalletData({
        fullName: user.fullName || "N/A",
        mobile: user.mobile || "N/A",
        walletBalance:
          (walletDataFromAPI.depositBalance || 0) +
          (walletDataFromAPI.winningBalance || 0) +
          (walletDataFromAPI.bonusBalance || 0) +
          (walletDataFromAPI.cashbackBalance || 0),
        depositBalance: walletDataFromAPI.depositBalance || 0,
        winningBalance: walletDataFromAPI.winningBalance || 0,
        bonusBalance: walletDataFromAPI.bonusBalance || 0,
        cashbackBalance: walletDataFromAPI.cashbackBalance || 0,
        lockedBalance: walletDataFromAPI.lockedBalance || 0,
        createdAt: walletDataFromAPI.createdAt || user.createdAt || new Date(),
        updatedAt: walletDataFromAPI.updatedAt || new Date(),
        transactions: walletDataFromAPI.transactions || [],
      });

      setShowWalletModal(true);
    } catch (error) {
      console.error("Wallet fetch error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Could not fetch wallet data",
      });
    } finally {
      setWalletLoading(false);
    }
  };

  const handleViewUser = async (user) => {
    try {
      setViewLoading(true);
      const response = await getUserById({ userId: user._id });
      console.log("User Details Response:", response);
      const userData = response.data || response;
      setEditData(userData);
      setViewMode(true);
      setShowAllUserListModal(true);
    } catch (error) {
      console.error("View user error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Could not fetch user details",
      });
    } finally {
      setViewLoading(false);
    }
  };

  const handleEditUser = async (user) => {
    try {
      setViewLoading(true);
      const response = await getUserById({ userId: user._id });
      console.log("Edit User Response:", response);
      const userData = response.data || response;
      setEditData(userData);
      setViewMode(false);
      setShowAllUserListModal(true);
    } catch (error) {
      console.error("Edit user error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Could not fetch user details",
      });
    } finally {
      setViewLoading(false);
    }
  };

  const handleDepositSuccess = () => {
    userList();
  };

  const handleViewUserDetails = (userId) => {
    navigate(`/user-details/${userId}`);
  };

  // ✅ Pagination handler functions
  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setPage(pageNumber);
    }
  };

  const goToPreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const goToNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  // ✅ Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;

    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  // ✅ Clear all filters
  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setKycStatus("");
    setCategoryFilter("");
    setMinValue("");
    setMaxValue("");
    setPage(1);
  };

  // ✅ Check if any filter is active
  const hasActiveFilters =
    search || status || kycStatus || categoryFilter || minValue || maxValue;

  // ✅ Generate category options from image data (1-383)
  const categoryOptions = Array.from({ length: 383 }, (_, i) => i + 1);

  return (
    <div className="lead-page">
      <PageHeader
        title="All User List"
      />
      <div className="card mb-4 card-light">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2 align-items-center">
            {showFilter && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search User..."
                  value={search}
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                  style={{ width: "180px" }}
                />

                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => {
                    setPage(1);
                    setStatus(e.target.value);
                  }}
                  style={{ width: "140px" }}
                >
                  <option value="">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>

                <select
                  className="form-select"
                  value={kycStatus}
                  onChange={(e) => {
                    setPage(1);
                    setKycStatus(e.target.value);
                  }}
                  style={{ width: "140px" }}
                >
                  <option value="">All KYC</option>
                  <option value="VERIFIED">Verified</option>
                  <option value="PENDING">Pending</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="NOT_SUBMITTED">Not Submitted</option>
                </select>

                {/* ✅ Category Filter from Image Data */}
                <select
                  className="form-select"
                  value={categoryFilter}
                  onChange={(e) => {
                    setPage(1);
                    setCategoryFilter(e.target.value);
                  }}
                  style={{ width: "150px" }}
                >
                  <option value="">All Categories</option>
                  <option value="1-100">1-100</option>
                  <option value="101-200">101-200</option>
                  <option value="201-300">201-300</option>
                  <option value="301-383">301-383</option>
                </select>

                {/* ✅ Value Range Filters */}
                <input
                  type="number"
                  className="form-control"
                  placeholder="Min Value"
                  value={minValue}
                  onChange={(e) => {
                    setPage(1);
                    setMinValue(e.target.value);
                  }}
                  style={{ width: "100px" }}
                />
                <input
                  type="number"
                  className="form-control"
                  placeholder="Max Value"
                  value={maxValue}
                  onChange={(e) => {
                    setPage(1);
                    setMaxValue(e.target.value);
                  }}
                  style={{ width: "100px" }}
                />

                {/* ✅ Clear Filters Button */}
                {hasActiveFilters && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    <FaTimes /> Clear
                  </button>
                )}
              </div>
            )}
            <button
              className={`btn ${showFilter ? "btn-primary" : "btn-dark"}`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter />
              {showFilter ? " Hide" : " Filter"}
            </button>
          </div>
        </div>
        <div className="card-body">
          {/* ✅ Show total items count and active filters */}
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <span className="text-muted">
                Showing {AllusersList.length} of {totalItems} users
              </span>
              {hasActiveFilters && (
                <span className="badge bg-info ms-2">Filters Applied</span>
              )}
            </div>
            <div>
              <span className="text-muted">
                Page {page} of {totalPages}
              </span>
            </div>
          </div>

          {/* ✅ Active Filters Display */}
          {hasActiveFilters && (
            <div className="mb-3 d-flex gap-2 flex-wrap">
              {search && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Search: {search}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setSearch("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {status && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Status: {status}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setStatus("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {kycStatus && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  KYC: {kycStatus}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setKycStatus("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {categoryFilter && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Category: {categoryFilter}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setCategoryFilter("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {minValue && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Min: {minValue}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setMinValue("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {maxValue && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Max: {maxValue}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setMaxValue("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}

          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Level</th>
                  <th>Referral</th>
                  <th>KYC</th>
                  <th>Status</th>
                  <th>Ledger</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && AllusersList.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : AllusersList.length > 0 ? (
                  AllusersList.map((item, index) => (
                    <tr key={item._id}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {item.profileImage ? (
                            <img
                              src={item.profileImage}
                              width="35"
                              height="35"
                              className="rounded-circle"
                              alt=""
                            />
                          ) : (
                            <div
                              className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                              style={{ width: "35px", height: "35px" }}
                            >
                              {item.fullName?.charAt(0) || "U"}
                            </div>
                          )}
                          <span>{item.fullName || "N/A"}</span>
                        </div>
                      </td>
                      <td>
                        <FaPhoneAlt size={12} /> {item.mobile}
                      </td>
                      <td>
                        <span className="badge bg-info">
                          {item.vipLevel}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-info">
                          {item.referralCode}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`badge ${
                            item.isKycVerified === "VERIFIED"
                              ? "bg-success"
                              : item.isKycVerified === "PENDING"
                                ? "bg-warning"
                                : item.isKycVerified === "REJECTED"
                                  ? "bg-danger"
                                  : "bg-secondary"
                          }`}
                        >
                          {item.isKycVerified === "VERIFIED"
                            ? "Verified"
                            : item.isKycVerified === "PENDING"
                              ? "Pending"
                              : item.isKycVerified === "REJECTED"
                                ? "Rejected"
                                : item.isKycVerified || "N/A"}
                        </span>
                      </td>
                      <td>
                        <div className="toggle-switch-container">
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={item.status === "ACTIVE"}
                              onChange={() =>
                                handleStatusToggle(item._id, item.status)
                              }
                              disabled={togglingId === item._id}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                          <span
                            className={`status-text ${item.status === "ACTIVE" ? "active-text" : "inactive-text"}`}
                          >
                            {/* {item.status === "ACTIVE" ? "Active" : "Inactive"} */}
                          </span>
                        </div>
                      </td>
                      <td>
                        <Link
                          to={`/ledger/${item._id}`}
                          className="btn btn-sm btn-info custum_info"
                        >
                          <LuCalculator />
                        </Link>
                      </td>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => {
                              setDepositUserId(item._id);
                              setShowAddDepositModal(true);
                            }}
                            title="Add Wallet Amount"
                          >
                            <FaPlus /> ₹
                          </button>

                          <button
                            className="btn btn-sm btn-info custum_info"
                            onClick={() => openWalletModal(item)}
                            disabled={walletLoading}
                            title="View Wallet"
                          >
                            {walletLoading ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : (
                              <FaWallet />
                            )}
                          </button>

                          <button
                            className="btn btn-sm btn-info custum_primary"
                            onClick={() => handleViewUserDetails(item._id)}
                            disabled={viewLoading}
                            title="View User Details"
                          >
                            {viewLoading ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : (
                              <FaEye />
                            )}
                          </button>

                          <button
                            className="btn btn-sm btn-info custum_danger"
                            onClick={() => {
                              setDeleteId(item._id);
                              setShowDeleteModal(true);
                            }}
                            title="Delete User"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center">
                      {hasActiveFilters
                        ? "No users found matching your filters"
                        : "No Users Found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ✅ Enhanced Pagination with Page Numbers */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
              <button
                className="btn btn-outline-secondary"
                onClick={goToPreviousPage}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>

              <div className="d-flex gap-1">
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn ${pageNum === page ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => goToPage(pageNum)}
                    disabled={loading}
                    style={{ minWidth: "40px" }}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-secondary"
                onClick={goToNextPage}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          )}

          {/* ✅ Simple pagination info */}
          <div className="d-flex justify-content-center mt-2">
            <small className="text-muted">
              Showing {AllusersList.length} of {totalItems} users | Page {page}{" "}
              of {totalPages}
            </small>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddDepositModal
        show={showAddDepositModal}
        setShow={setShowAddDepositModal}
        userId={depositUserId}
        onSuccess={handleDepositSuccess}
      />

      <AllUserListModal
        show={showAllUserListModal}
        setShow={setShowAllUserListModal}
        editData={editData}
        viewMode={viewMode}
      />

      <DeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        title="Delete User?"
        message="Are you sure you want to delete this user?"
      />

      <WalletModal
        show={showWalletModal}
        setShow={setShowWalletModal}
        editData={walletData}
      />
    </div>
  );
};

export default AllUserList;
